import { useEffect, useSyncExternalStore } from 'react';
import { delay } from '@planitgeo/treeplotter-domain/lib/infrastructure/retry/retry-utils';
import { Tp2Id, Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';
import { createWorkTask, linkPhotosToWorkTask } from 'api-client';
import WorkTaskRepository from 'common/infrastructure/work-task/repository';
import TenantRepository from 'common/infrastructure/tenant/repository';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import {
  queryClient as globalQueryClient,
  reportUnreachableError,
} from 'common/infrastructure/remoteState/persistQueryClient';
import { onlineManager } from '@tanstack/react-query';
import { WorkTaskDtoWithPhotos } from 'common/infrastructure/work-task/types';
import { buildCreateWorkTaskPayload } from 'components/work/create-work/createWork.payload';
import { isWorkTaskReady, areWorkTaskPhotosAttached } from './workTaskReadiness';
import {
  WorkSubmission,
  WorkSubmissionFailureStep,
  getWorkSubmissions,
  workSubmissionPhase,
  markWorkCreated,
  markSubmissionPhotosLinked,
  bumpWorkSubmissionAttempts,
  setWorkSubmissionError,
  removeWorkSubmission,
  subscribeWorkSubmissions,
  emitWorkSubmissionFailure,
  getPhotoUploadOutcome,
} from './workSubmissionOutbox';

/**
 * App-wide runner that drains the durable {@link WorkSubmission} outbox. It is the sole
 * owner of the two non-durable steps of the create-work chain — creating the work task
 * and linking its photos — and it resumes them, in dependency order, across navigation,
 * reconnects, and app restarts. The durable steps (host submit, photo upload) are owned
 * by persisted TanStack mutations; the runner only reacts to the flags they flip in the
 * outbox.
 *
 * Per pass, each entry is advanced by its derived phase:
 *   awaiting-host → skip (the host mutation's success flips hostDone)
 *   creating      → POST create-work-task with the now-known asset context, mark created
 *   reconciling   → poll the CQRS read model for tp2WorkRecordId, then (once all photos
 *                   have settled) link the uploaded ones and wait for them to attach
 *
 * Give-up is by GENUINE error only: a thrown API call while online bumps the entry's
 * attempt counter and, past {@link MAX_ONLINE_ATTEMPTS}, drops it with a failure toast.
 * A clean "not ready yet" (CQRS lag, a photo still uploading) never counts as an attempt
 * — those waits are legitimate and can be long (esp. offline) and are bounded only by the
 * outbox TTL. Offline passes are skipped wholesale so a lack of connection is never
 * mistaken for a failure.
 */

/** Drop a submission after this many failed ONLINE attempts (thrown API errors). */
export const MAX_ONLINE_ATTEMPTS = 8;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isOnline(): boolean {
  // onlineManager, not navigator.onLine: it tracks the browser's events plus the evidence
  // of requests that failed without a response, which navigator.onLine can miss entirely.
  return onlineManager.isOnline();
}

/**
 * Count a failed online attempt for an entry; drop it (with a failure toast) once it has
 * exhausted its attempts. Returns true when the entry was given up on.
 */
function recordFailure(
  entry: WorkSubmission,
  step: WorkSubmissionFailureStep,
  error: unknown
): boolean {
  const message = errorMessage(error);

  // A failure with no HTTP response never reached the server, so it is a connectivity
  // problem rather than a bad submission and must not consume an attempt — otherwise a
  // long outage silently discards the user's work.
  if (reportUnreachableError(error)) {
    setWorkSubmissionError(entry.workTaskId, message);
    loggerService.log(
      LoggerLevel.WARN,
      `Work submission ${entry.workTaskId} could not reach the server at step "${step}"; waiting`,
      error
    );
    return false;
  }

  const attempts = entry.attempts + 1;
  bumpWorkSubmissionAttempts(entry.workTaskId);
  setWorkSubmissionError(entry.workTaskId, message);
  loggerService.log(
    LoggerLevel.WARN,
    `Work submission ${entry.workTaskId} failed at step "${step}" (attempt ${attempts})`,
    error
  );
  if (attempts >= MAX_ONLINE_ATTEMPTS) {
    emitWorkSubmissionFailure({
      workTaskId: entry.workTaskId,
      hostKind: entry.hostKind,
      tp2AssetId: entry.tp2AssetId,
      step,
      message,
    });
    removeWorkSubmission(entry.workTaskId);
    return true;
  }
  return false;
}

/** creating phase: create the work task now that the asset context is known. */
async function advanceCreating(entry: WorkSubmission, tenantId: string): Promise<void> {
  // tp2AssetId is known by now for every flow (up front, or from the createAsset
  // response). If it somehow isn't, we can't build a correct payload and there is no
  // read API to recover it — leave the entry to wait (TTL-bounded) rather than create
  // work against a stale/absent asset id.
  if (entry.assetId == null || entry.tp2AssetId == null) return;

  const data = buildCreateWorkTaskPayload({
    context: { assetId: entry.assetId as Tp3Id, tp2AssetId: entry.tp2AssetId as Tp2Id },
    values: entry.values,
    taskType: entry.taskType,
  });
  await createWorkTask(tenantId, entry.workTaskId, data);
  markWorkCreated(entry.workTaskId);
}

/**
 * reconciling phase (batched): poll the read model for all reconciling entries at once,
 * then per entry link uploaded photos and check attachment. Mirrors the original
 * single-tracker reconciler, but gated per submission and on per-photo upload status.
 */
async function reconcileWorkTasks(entries: WorkSubmission[], tenantId: string): Promise<void> {
  const found = (await WorkTaskRepository.findWorkTasksByIds(
    entries.map((entry) => entry.workTaskId),
    tenantId
  )) as WorkTaskDtoWithPhotos[];
  const byId = new Map(found.map((task) => [task.id, task]));

  const doneIds: string[] = [];
  for (const entry of entries) {
    const task = byId.get(entry.workTaskId);
    if (!task || !isWorkTaskReady(task)) continue; // no tp2WorkRecordId yet → wait (no attempt)

    // Look each photo's upload outcome up by id from the durable outcome store (a photo
    // may have finished uploading before this entry even existed). Wait until every photo
    // has settled (uploaded or failed) before linking, so one link call covers them all —
    // a later upload can't be stranded by an early link. Failed uploads are skipped.
    const outcomes = entry.photos.map((photo) => getPhotoUploadOutcome(photo.photoId));
    const stillUploading = outcomes.some((outcome) => outcome === undefined);
    if (stillUploading) continue; // an upload is still in flight → wait (no attempt)

    const uploadedIds = entry.photos
      .filter((_, index) => outcomes[index] === 'uploaded')
      .map((photo) => photo.photoId);

    if (uploadedIds.length > 0 && !entry.photosLinked) {
      try {
        await linkPhotosToWorkTask(tenantId, entry.workTaskId, { photos: uploadedIds });
        markSubmissionPhotosLinked(entry.workTaskId);
      } catch (error) {
        recordFailure(entry, 'photos', error);
        continue;
      }
    }

    if (uploadedIds.length > 0 && !areWorkTaskPhotosAttached(task, uploadedIds)) continue; // attach pending → wait

    doneIds.push(entry.workTaskId); // no photos, or all uploaded photos attached
  }

  if (doneIds.length > 0) {
    // Refresh the lists first (so the tasks appear), then drop the ids (which is what
    // stops the sync indicator).
    await globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasks'] });
    await globalQueryClient.invalidateQueries({ queryKey: ['findWorkTasksByAssetIds'] });
    doneIds.forEach((id) => removeWorkSubmission(id));
  }
}

/** One pass over the whole outbox: creating entries first, then reconciling ones. */
async function reconcileSubmissionsOnce(): Promise<void> {
  const tenantId = TenantRepository.getCurrentTenantPid();
  if (!tenantId || !isOnline()) return; // offline waits are not attempts

  const submissions = getWorkSubmissions();
  if (submissions.length === 0) return;

  // Resume any paused/persisted mutations (photo uploads, host submits) whenever there's
  // queued work. TanStack only auto-resumes on app restore + the browser's 'online' event,
  // so an upload that paused on a transient blip could otherwise sit forever and never
  // record its outcome. Fire-and-forget: the completion writes the outcome store / outbox,
  // which re-triggers this runner to link.
  void globalQueryClient.resumePausedMutations();

  // Create-work is a prerequisite for its own reconciliation, so run all creating entries
  // first; markWorkCreated moves them into the reconciling set read back below.
  for (const entry of submissions) {
    if (workSubmissionPhase(entry) !== 'creating') continue;
    try {
      await advanceCreating(entry, tenantId);
    } catch (error) {
      recordFailure(entry, 'work', error);
    }
  }

  const reconciling = getWorkSubmissions().filter(
    (entry) => workSubmissionPhase(entry) === 'reconciling'
  );
  if (reconciling.length > 0) {
    await reconcileWorkTasks(reconciling, tenantId);
  }
}

// The loop lives at module scope — NOT inside a React effect — so a re-render, route
// change, or the creating screen unmounting can't abort it. A `running` guard makes
// re-triggers idempotent; the in-flight loop picks up newly-flipped entries live because
// each pass re-reads the outbox.
let running = false;
let activelySyncing = false;
const activeSyncListeners = new Set<() => void>();

function setActivelySyncing(value: boolean): void {
  if (activelySyncing === value) return;
  activelySyncing = value;
  activeSyncListeners.forEach((listener) => listener());
}

/** Whether a drain pass is currently running (for the sync spinner/banner). */
export function useIsWorkSubmissionSyncing(): boolean {
  return useSyncExternalStore(
    (listener) => {
      activeSyncListeners.add(listener);
      return () => {
        activeSyncListeners.delete(listener);
      };
    },
    () => activelySyncing,
    () => activelySyncing
  );
}

// Steady, responsive reconciliation cadence. We poll the CQRS read model on a short FIXED
// interval (not exponential backoff — that made late polls tens of seconds apart), and the
// wait is cut short the instant the outbox changes (host submit acked, photo uploaded, work
// created, photos linked), so each step chains into the next immediately instead of waiting
// out the interval. The loop runs until the outbox drains, we go offline (the 'online' event
// re-triggers), or nothing has changed for MANY consecutive polls (a genuinely stuck entry —
// a later foreground/reconnect/submit re-triggers it, and the outbox TTL is the final bound).
const POLL_INTERVAL_MS = 1500;
const MAX_IDLE_PASSES = 60; // ~90s of no progress before backing off to event-triggered only

/**
 * A delay that resolves early as soon as the outbox changes, so a completed step (which
 * writes the outbox) chains into the next pass without waiting out the interval. The timeout
 * leg uses `delay` so tests can mock it to run the loop instantly.
 */
function interruptibleDelay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve();
    };
    const unsubscribe = subscribeWorkSubmissions(finish);
    void delay(ms).then(finish);
  });
}

/** A signature of all forward progress across the outbox: the phase flags plus how many of
 *  each entry's photos have a recorded upload outcome. Unchanged across a pass ⇒ no progress. */
function progressSignature(): string {
  return getWorkSubmissions()
    .map((entry) => {
      const settled = entry.photos.filter(
        (photo) => getPhotoUploadOutcome(photo.photoId) !== undefined
      ).length;
      const flags = `${entry.hostDone ? 1 : 0}${entry.workCreated ? 1 : 0}${
        entry.photosLinked ? 1 : 0
      }`;
      return `${entry.workTaskId}:${flags}:${settled}/${entry.photos.length}`;
    })
    .join('|');
}

export async function runWorkSubmissionSync(): Promise<void> {
  if (running) return;
  running = true;
  setActivelySyncing(true);
  let idlePasses = 0;
  let prevSignature = progressSignature();
  try {
    while (getWorkSubmissions().length > 0 && isOnline() && idlePasses < MAX_IDLE_PASSES) {
      await reconcileSubmissionsOnce();
      if (getWorkSubmissions().length === 0) break;

      const signature = progressSignature();
      if (signature !== prevSignature) {
        idlePasses = 0; // something advanced → keep polling promptly
        prevSignature = signature;
      } else {
        idlePasses += 1; // nothing changed this pass → count toward the stuck-entry cap
      }
      await interruptibleDelay(POLL_INTERVAL_MS);
    }
  } catch (error) {
    // Give up this run; entries stay for a later trigger (a flipped flag, foreground,
    // reconnect, or next app open) to retry, bounded by the outbox TTL.
    loggerService.log(LoggerLevel.WARN, 'Work submission sync did not converge', error);
  } finally {
    running = false;
    setActivelySyncing(false);
  }
}

/** Kick a drain pass (idempotent). Safe to call programmatically anytime. */
export function triggerWorkSubmissionSync(): void {
  void runWorkSubmissionSync();
}

/**
 * App-wide drain runner. Mount ONCE (headless, at the app root). Kicks a pass whenever
 * the outbox changes (a host submit acked, a photo uploaded, a new submission enqueued),
 * on foreground, and on reconnect — so the chain advances regardless of which route is
 * open. The pass itself runs at module scope, unaffected by this component's lifecycle.
 */
export function useWorkSubmissionSyncRunner(): void {
  useEffect(() => {
    const unsubscribe = subscribeWorkSubmissions(() => {
      if (getWorkSubmissions().length > 0) triggerWorkSubmissionSync();
    });
    // Kick once on mount for work persisted from a previous app session.
    if (getWorkSubmissions().length > 0) triggerWorkSubmissionSync();
    return unsubscribe;
  }, []);

  useEffect(() => {
    const onWake = () => {
      if (getWorkSubmissions().length > 0) triggerWorkSubmissionSync();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onWake();
    };
    document.addEventListener('visibilitychange', onVisibility);
    // onlineManager rather than the window event, so a recovery detected by the
    // reachability probe also restarts the runner.
    const unsubscribeOnline = onlineManager.subscribe((online) => {
      if (online) onWake();
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribeOnline();
    };
  }, []);
}
